import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Alert, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faLock,
  faGlobe,
  faUserCog
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCommunity } from '../../services/communityService';
import { getEventTypeDisplayName } from '../../utils/EventTypesUtil';
import './styles/MobileCommunityView.css';

function MobileCommunityView() {
  const { communityId } = useParams();
  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [supportedEventTypes, setSupportedEventTypes] = useState([]);
  const { user, isAdminOfCommunity, isMemberOfCommunity } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        setScrollY(contentRef.current.scrollTop);
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Fetch community data
  useEffect(() => {
    const fetchCommunityData = async () => {
      if (!communityId) return;
      
      try {
        setLoading(true);
        setError('');
        
        const communityData = await getCommunity(communityId);
        if (communityData) {
          setCommunity(communityData);
          
          // Parse supported event types
          if (communityData.supported_event_types) {
            try {
              const eventTypes = JSON.parse(communityData.supported_event_types);
              setSupportedEventTypes(eventTypes);
            } catch (error) {
              setSupportedEventTypes([]);
            }
          } else {
            setSupportedEventTypes([]);
          }
        } else {
          setError(t('communities.errors.fetchError'));
        }
      } catch (err) {
        setError(t('communities.errors.fetchError'));
      } finally {
        setLoading(false);
      }
    };

    fetchCommunityData();
  }, [communityId, t]);

  // Function to handle navigation back
  const handleGoBack = () => {
    navigate(-1);
  };

  // Function to navigate to community management
  const handleManageCommunity = () => {
    navigate(`/manage-community/${communityId}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="community-view-loading">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
        <p>{t('communities.loading')}</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="community-view-error">
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-secondary" onClick={handleGoBack}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          {t('navigation.back')}
        </Button>
      </div>
    );
  }

  // If community is not found
  if (!community) {
    return (
      <div className="community-view-error">
        <Alert variant="warning">{t('communities.errors.fetchError')}</Alert>
        <Button variant="outline-secondary" onClick={handleGoBack}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          {t('navigation.back')}
        </Button>
      </div>
    );
  }

  const isAdmin = isAdminOfCommunity(parseInt(communityId));
  const isMember = isMemberOfCommunity(parseInt(communityId));

  return (
    <div className="mobile-community-view">
      <div className="community-scrollable-content" ref={contentRef}>
        {/* Header */}
        <div className="community-header-container" 
          style={{
            borderBottomLeftRadius: scrollY > 50 ? '20px' : '0',
            borderBottomRightRadius: scrollY > 50 ? '20px' : '0',
            boxShadow: scrollY > 50 ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
            backgroundColor: '#121212'
          }}>
          <div className="community-header-content">
            <Button 
              variant="link" 
              className="p-0 text-white back-btn" 
              onClick={handleGoBack}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <div className="community-info">
              <h1 className="community-title">{community.name}</h1>
              <div className="community-meta">
                {community.visibility === 'Private' ? (
                  <div className="community-visibility">
                    <FontAwesomeIcon icon={faLock} size="xs" />
                    <span>{t('events.visibility.private')}</span>
                  </div>
                ) : (
                  <div className="community-visibility">
                    <FontAwesomeIcon icon={faGlobe} size="xs" />
                    <span>{t('events.visibility.public')}</span>
                  </div>
                )}
              </div>
              {community.description && (
                <div className="community-description">
                  {community.description}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="community-content">
          <div className="community-section">
            <div className="section-item">
              <div className="section-label">{t('communities.memberEditing')}</div>
              <div className="section-value">
                {community.allow_member_edits ? (
                  <Badge bg="success">{t('communities.enabled')}</Badge>
                ) : (
                  <Badge bg="secondary">{t('communities.disabled')}</Badge>
                )}
              </div>
            </div>

            <div className="section-item">
              <div className="section-label">{t('communities.eventTypes')}</div>
              <div className="section-value">
                {supportedEventTypes.length > 0 ? (
                  <div className="event-types-container">
                    {supportedEventTypes.map((type, index) => (
                      <Badge key={index} bg="light" text="dark" className="event-type-badge">
                        {getEventTypeDisplayName(type, t)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted">{t('events.messages.noCommunityEventTypes')}</span>
                )}
              </div>
            </div>

            {isAdmin && (
              <div className="admin-actions">
                <Button
                  variant="primary"
                  onClick={handleManageCommunity}
                  className="manage-community-btn"
                >
                  <FontAwesomeIcon icon={faUserCog} className="me-2" />
                  {t('profile.manageCommunities')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MobileCommunityView;